import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Loader2, UserPlus, CheckCircle, Clock } from 'lucide-react';

function Signup() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [needsApproval, setNeedsApproval] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const result = await signup(email, password, name);
    
    if (result.success) {
      setSuccess(true);
      setNeedsApproval(!result.approved);
      
      if (result.approved) {
        setTimeout(() => navigate('/login'), 2000);
      }
    }
    
    setLoading(false);
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0a0a0a] via-[#1a1a2e] to-[#0a0a0a] p-4">
        <Card className="w-full max-w-md bg-white/5 border-white/10 backdrop-blur-lg">
          <CardContent className="pt-6 text-center">
            {needsApproval ? (
              <>
                <Clock className="w-16 h-16 mx-auto mb-4 text-yellow-400" />
                <h2 className="text-2xl font-bold text-white mb-2">
                  Waiting for Approval
                </h2>
                <p className="text-white/60 mb-6">
                  Your account has been created successfully!
                  <br />
                  Please wait for an admin to approve your account.
                </p>
              </>
            ) : (
              <>
                <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-400" />
                <h2 className="text-2xl font-bold text-white mb-2">
                  Account Created!
                </h2>
                <p className="text-white/60 mb-6">
                  Your admin account is ready. Redirecting to login...
                </p>
              </>
            )}
            <Button
              onClick={() => navigate('/login')}
              className="bg-[#00d4aa] hover:bg-[#00e6bb] text-black"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0a0a0a] via-[#1a1a2e] to-[#0a0a0a] p-4">
      <Card className="w-full max-w-md bg-white/5 border-white/10 backdrop-blur-lg">
        <CardHeader className="text-center">
          <div className="font-mono text-[#00d4aa] text-sm uppercase tracking-wider mb-2">
            EPSILLON MEDIA
          </div>
          <CardTitle className="text-3xl font-bold text-white">
            Create Account
          </CardTitle>
          <CardDescription className="text-white/60">
            Sign up for Content Repurposing Hub
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-white/80">
                Full Name
              </Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="bg-white/5 border-white/10 text-white mt-2"
                placeholder="John Doe"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-white/80">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-white/5 border-white/10 text-white mt-2"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-white/80">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="bg-white/5 border-white/10 text-white mt-2"
                placeholder="••••••••"
              />
              <p className="text-xs text-white/40 mt-1">Minimum 6 characters</p>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#00d4aa] hover:bg-[#00e6bb] text-black font-semibold"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating account...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Sign Up
                </>
              )}
            </Button>

            <div className="text-center text-sm text-white/60">
              Already have an account?{' '}
              <Link to="/login" className="text-[#00d4aa] hover:underline">
                Sign in
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default Signup;